import {
  AfterViewInit,
  ChangeDetectorRef,
  Component,
  ElementRef,
  EventEmitter,
  HostBinding,
  HostListener,
  Input,
  Output,
  ViewChild,
} from '@angular/core';
import { NgTemplateOutlet } from '@angular/common';
import { AbstractControl, ReactiveFormsModule } from '@angular/forms';
import { TranslatePipe } from '@ngx-translate/core';
import { InputControlBase } from '../../forms/input-control.base';
import { AstralFieldShellComponent } from '../astral-field-shell/astral-field-shell.component';
import { resolveAstralIconClass } from '../astral-icon-class.util';
import { AstralFieldAccessibilityDirective } from '../astral-field-accessibility.util';

@Component({
  selector: 'astral-textarea',
  standalone: true,
  imports: [NgTemplateOutlet, ReactiveFormsModule, TranslatePipe, AstralFieldShellComponent, AstralFieldAccessibilityDirective],
  templateUrl: './astral-textarea.component.html',
  styleUrls: ['./astral-textarea.component.scss'],
})
export class AstralTextareaComponent extends InputControlBase<string> implements AfterViewInit {
  private static nextId = 0;
  private readonly uid = `astral-textarea-${++AstralTextareaComponent.nextId}`;

  constructor(private readonly cd: ChangeDetectorRef) {
    super();
  }

  @Input() accessibilityControl: AbstractControl | null;
  @Input() focused = false;
  @Input() inputId: string;
  @Input() inputName: string;
  @Input() nameplate = false;
  @Input() maxLength: number;
  @Input() placeholderText: string;
  @Input() rows = 5;
  @Input() showCharacterCount = false;
  @Input() span: number | 'full' = 1;
  @Input() withShell = true;

  @Output() blur = new EventEmitter<void>();

  @ViewChild('textarea') textarea: ElementRef<HTMLTextAreaElement>;

  isFocused = false;

  @HostBinding('tabindex') tabindex = -1;

  @HostBinding('style.grid-column')
  get hostGridColumn(): string | null {
    if (this.span === 'full') {
      return '1 / -1';
    }

    if (!this.span || this.span <= 1) {
      return null;
    }

    return `span ${this.span}`;
  }

  get resolvedInputId(): string {
    return this.inputId ?? this.uid;
  }

  get resolvedInputName(): string {
    if (this.inputName) {
      return this.inputName;
    }

    const caption = this.control?.caption
      ?.toLowerCase()
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-+|-+$/g, '');

    return caption || this.uid;
  }

  get resolvedPlaceholderText(): string | null {
    return this.placeholderText ?? this.control?.caption ?? null;
  }

  get characterCountId(): string {
    return `${this.resolvedInputId}-character-count`;
  }

  get currentLength(): number {
    return this.control?.value?.length ?? 0;
  }

  get displaysCharacterCount(): boolean {
    return this.showCharacterCount && this.maxLength != null;
  }

  get readonlyIconClass(): string {
    return resolveAstralIconClass('lock') ?? '';
  }

  @HostListener('focus')
  focusHost() {
    this.textarea?.nativeElement.focus();
  }

  ngAfterViewInit(): void {
    if (this.focused) {
      this.textarea?.nativeElement.focus();
      this.isFocused = true;
      this.cd.detectChanges();
    }
  }

  handleBlur(): void {
    this.isFocused = false;
    super.onblur();
    if (this.control && !this.control.touched) {
      this.control.markAsTouched();
    }
    this.blur.emit();
  }

  handleFocus(): void {
    this.isFocused = true;
    this.onfocus();
  }

  onInput(event: Event): void {
    if (!this.nameplate) {
      return;
    }

    const textarea = event.target as HTMLTextAreaElement;
    const parts = textarea.value.split(/\r?\n/);
    if (parts.length > 2) {
      textarea.value = parts.slice(0, 2).join('\n');
    }
    this.control.setValue(textarea.value);
  }

  onKeyDown(event: KeyboardEvent): void {
    if (!this.nameplate) {
      return;
    }

    const textarea = event.target as HTMLTextAreaElement;
    if (event.key === 'Enter' && (textarea.value.match(/\n/g) || []).length >= 1) {
      event.preventDefault();
    }

    const allowedKeys = /^[a-zA-ZåäöÅÄÖ0-9 &.,!?\-']$/;
    if (event.key.length === 1 && !allowedKeys.test(event.key) && event.key !== 'Enter') {
      event.preventDefault();
    }
  }
}
